import { Primitive } from '@sentry/core'
import { withScope } from '@sentry/nextjs'
import basicAxios, { AxiosError, AxiosRequestConfig, Method } from 'axios'
import { throttle } from 'lodash'

import { notification } from '@/components/shared/message'
import { clientAxiosError } from '@/constants/sentry-type'
import { SECOND } from '@/constants/unit'
import { ErrorResponse } from '@/utils/api'
import { getValidToken } from '@/utils/auth'
import {
  getApiBaseUrl,
  getBaeApiBaseUrl,
  getBaeCopeApiBaseUrl,
  getEnv,
  isTestEmployee,
  production,
} from '@/utils/env'

const defaultErrorMessage = 'System error. Please try again later.'

const throttledNotification = throttle(
  (message: string) => notification.error({ message }),
  2 * SECOND,
)

export const axios = basicAxios.create()
const baeAxios = basicAxios.create()

const baeTokenMap: Map<number, string> = new Map()
export const initBaeTokenMap = ({
  creatorId,
  token,
}: {
  creatorId: number
  token?: string
}) => {
  if (token) {
    baeTokenMap.set(creatorId, token)
  } else {
    baeTokenMap.delete(creatorId)
  }
}

export const getBaeTokenByCreatorId = (creatorId: number) => {
  return baeTokenMap.get(creatorId)
}

export interface BaeAxiosRequestParam {
  creatorId: number
  url: string
  method?: Method
  data?: unknown
  headers?: Record<string, string>
}

export const requestBaeApi = async<T = unknown>({
  creatorId,
  url,
  method = 'GET',
  data,
  headers,
}: BaeAxiosRequestParam) => {
  const isCopeApiUrl = url.includes('/co/')
  const config: AxiosRequestConfig = {
    baseURL: isCopeApiUrl ? getBaeCopeApiBaseUrl() : getBaeApiBaseUrl(),
    method,
    data,
  }

  config.headers = headers ?? {}
  const baeToken = baeTokenMap.get(creatorId)
  if (baeToken) {
    config.headers.Authorization = `Bearer ${baeToken}`
  }
  if (isCopeApiUrl && !('X-API-KEY' in config.headers)) {
    config.headers['X-API-KEY'] =
      getEnv() === production
        ? 'c3cd345afcd7b3bb71c958b65ea590247bc9cb84d159ce81bd771c8710b5801c'
        : 'c3cd345afcd7b3bb71c958b65ea590247bc9cb84d159ce81bd771c8710b5801c'
  }

  return await baeAxios<T>(url, config)
}

axios.interceptors.request.use(async (config) => {
  config.baseURL = getApiBaseUrl()
  const { idToken } = await getValidToken()
  config.headers.Authorization = idToken
  return config
})

const responseErrorInterceptor = async ({
  response,
}: AxiosError<ErrorResponse>) => {
  const tags: Record<string, Primitive> = {
    type: clientAxiosError,
  }
  try {
    if (response) {
      const {
        status,
        headers,
        config: { url },
        data: { message },
      } = response
      tags.status = status
      tags.url = url
      const requestId = headers['x-amzn-requestid'] as string | undefined
      tags.requestId = requestId
      tags.message = message
      throttledNotification(isTestEmployee ? `${message} (${url}, ${status}, ${requestId})` : `${defaultErrorMessage} (${requestId})`)
      withScope((scope) => {
        scope.setTags(tags)
        scope.captureMessage(clientAxiosError, 'error')
      })
      if (status === 401 || status === 403) {
        window.location.reload()
      }
    }
  } catch (e) {
    throttledNotification(defaultErrorMessage)
    withScope((scope) => {
      scope.setTags(tags)
      scope.captureException(e)
    })
  }
  return Promise.reject(response?.data)
}

axios.interceptors.response.use(
  (response) => response.data,
  responseErrorInterceptor,
)

baeAxios.interceptors.response.use(
  (response) => response,
  responseErrorInterceptor,
)
